import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, User, ArrowRight, Moon, Sun, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '@/hooks/useTheme';
import { loadFeatureFlags } from '@/components/utils/featureFlags';

export default function RoleSelection() {
  const { isDark, toggleTheme } = useTheme();
  const [featureFlags, setFeatureFlags] = useState({
    allow_company_registration: true,
    allow_individual_registration: true
  });

  useEffect(() => {
    loadFlags();
  }, []);

  const loadFlags = async () => {
    try {
      const flags = await loadFeatureFlags();
      setFeatureFlags(flags);
    } catch (err) {
      console.error('Failed to load feature flags:', err);
    }
  };
  
  const handleRoleSelect = async (role) => {
    // Check if registration is allowed for this role
    if (role === 'employer' && !featureFlags.allow_company_registration) {
      return; // Registration disabled
    }
    if (role === 'employee' && !featureFlags.allow_individual_registration) {
      return; // Registration disabled
    }

    if (role === 'employer') {
      window.location.href = '/CompanyOnboarding';
    } else {
      // Set user role to employee and create basic employee record
      const { api } = await import('@/api/apiClient');
      const user = await api.auth.me();
      
      // Update user role
      await api.auth.updateMe({ user_role: 'employee' });
      
      // Create basic employee record
      await api.entities.Employee.create({
        user_email: user.email,
        first_name: user.full_name?.split(' ')[0] || '',
        last_name: user.full_name?.split(' ').slice(1).join(' ') || '',
        enrollment_status: 'not_started'
      });
      
      window.location.href = '/EmployeeOnboarding';
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
      {/* Header */}
      <header className="bg-background/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">BenefitsPro</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      <div className="flex items-center justify-center p-6 pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl w-full"
        >
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Welcome to BenefitsPro!
            </h1>
            <p className="text-xl text-muted-foreground">
              Let's get you set up. Are you here as an employer or an employee?
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
          {/* Employee Option - FIRST & PROMINENT */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <Card className={`h-full hover:shadow-2xl transition-all ${!featureFlags.allow_individual_registration ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} border-4 border-blue-500 bg-gradient-to-br from-blue-50 to-cyan-50`}
                  onClick={() => featureFlags.allow_individual_registration && handleRoleSelect('employee')}>
              <CardContent className="p-10 flex flex-col items-center text-center space-y-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
                  <User className="w-12 h-12 text-white" />
                </div>
                
                <div>
                  <div className="inline-block px-3 py-1 rounded-full bg-blue-500 text-white text-xs font-semibold mb-2">
                    MOST COMMON
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">I'm an Employee</h3>
                  <p className="text-slate-700 mb-4 text-lg">
                    Access my company's benefits portal and enroll in coverage
                  </p>
                  {!featureFlags.allow_individual_registration && (
                    <p className="text-red-600 font-semibold text-sm">
                      Individual registration is currently disabled
                    </p>
                  )}
                </div>

                <div className="space-y-2 text-sm text-slate-700">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <span>Enter invitation code</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <span>Browse available benefits</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <span>Enroll and save on taxes</span>
                  </div>
                </div>

                <Button 
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-lg py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!featureFlags.allow_individual_registration}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (featureFlags.allow_individual_registration) {
                      handleRoleSelect('employee');
                    }
                  }}
                >
                  Continue as Employee
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Employer Option */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Card className={`h-full hover:shadow-xl transition-all ${!featureFlags.allow_company_registration ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} border-2 hover:border-emerald-500`}
                  onClick={() => featureFlags.allow_company_registration && handleRoleSelect('employer')}>
              <CardContent className="p-8 flex flex-col items-center text-center space-y-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                  <Building2 className="w-10 h-10 text-white" />
                </div>
                
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">I'm an Employer</h3>
                  <p className="text-slate-600 mb-4">
                    Set up benefits for my company and manage employee enrollments
                  </p>
                  {!featureFlags.allow_company_registration && (
                    <p className="text-red-600 font-semibold text-sm">
                      Company registration is currently disabled
                    </p>
                  )}
                </div>

                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span>Create company profile</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span>Invite and manage employees</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span>Configure benefits and policies</span>
                  </div>
                </div>

                <Button 
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!featureFlags.allow_company_registration}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (featureFlags.allow_company_registration) {
                      handleRoleSelect('employer');
                    }
                  }}
                >
                  Continue as Employer
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-8">
          Need help? Contact your HR department or reach out to support@benefitspro.com
        </p>
        </motion.div>
      </div>
    </div>
  );
}